/**
 * Pop-up pickers for jumping to a week, month or quarter. Each works in the
 * same periods as periodBounds(), and hands back the offset it takes, so a
 * pick lands exactly where the ‹ › pager would have.
 *
 *   WeekPicker     a month calendar; rows run Monday to Sunday, and clicking
 *                  any day picks the row it sits in
 *   MonthPicker    a year of twelve months
 *   QuarterPicker  a year of four quarters
 *
 * `marked` (days with time logged) puts a dot on each period that has any.
 * `latest` greys out periods that start after it — Reports doesn't page into
 * the future; Time › Entries does.
 */

import { useEffect, useMemo, useRef, useState } from "react";

const DAY = 86_400_000;

/** Local midnight of the Monday on or before `d`. */
export function mondayOf(d: Date): Date {
  const m = new Date(d);
  m.setHours(0, 0, 0, 0);
  m.setDate(m.getDate() - ((m.getDay() + 6) % 7));
  return m;
}

/** Whole weeks from this week's Monday to `d`'s: the offset periodBounds takes. */
export function weekOffsetOf(d: Date, now = new Date()): number {
  // Rounded, because a DST change makes the gap an hour short or long.
  return Math.round((mondayOf(d).getTime() - mondayOf(now).getTime()) / (7 * DAY));
}

export function monthOffsetOf(d: Date, now = new Date()): number {
  return (d.getFullYear() - now.getFullYear()) * 12 + d.getMonth() - now.getMonth();
}

export function quarterOffsetOf(d: Date, now = new Date()): number {
  const q = (x: Date) => x.getFullYear() * 4 + Math.floor(x.getMonth() / 3);
  return q(d) - q(now);
}

/** YYYY-MM-DD in local time, for matching days to `marked`. */
export const dayKey = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const MONTHS_SHORT = Array.from({ length: 12 }, (_, m) => new Date(2000, m, 1).toLocaleDateString(undefined, { month: "short" }));

/* -- Week ------------------------------------------------------------------ */

export function WeekPicker({ selected, marked, latest, onPick, onClose }: {
  /** Monday of the week shown, or null when none is (All time). */
  selected: Date | null;
  marked?: Set<string>;
  latest?: Date;
  onPick: (monday: Date) => void;
  onClose: () => void;
}) {
  const [month, setMonth] = useState(() => {
    const d = selected ?? new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [hover, setHover] = useState<number | null>(null);

  /** Every week touching this month, as rows of seven days. */
  const weeks = useMemo(() => {
    const rows: Date[][] = [];
    const last = new Date(month.getFullYear(), month.getMonth() + 1, 0);
    for (let start = mondayOf(month); start <= last; ) {
      rows.push(Array.from({ length: 7 }, (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i)));
      start = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 7);
    }
    return rows;
  }, [month]);

  const today = dayKey(new Date());
  const selectedKey = selected ? dayKey(selected) : null;
  const lastMonday = latest ? mondayOf(latest).getTime() : Infinity;

  return (
    <Popover label="Pick a week" onClose={onClose} width={272}>
      <Header
        title={month.toLocaleDateString(undefined, { month: "long", year: "numeric" })}
        prevLabel="Previous month" nextLabel="Next month"
        onShift={(n) => setMonth((m) => new Date(m.getFullYear(), m.getMonth() + n, 1))}
        nextDisabled={latest ? new Date(month.getFullYear(), month.getMonth() + 1, 1).getTime() > lastMonday + 6 * DAY : false}
      />

      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", marginBottom: 2 }}>
        {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
          <div key={i} style={{ textAlign: "center", fontSize: 10.5, color: "var(--muted2)", padding: "2px 0" }}>{d}</div>
        ))}
      </div>

      {weeks.map((row, r) => {
        const isSelected = dayKey(row[0]) === selectedKey;
        const future = row[0].getTime() > lastMonday;
        const lit = !future && (isSelected || hover === r);
        return (
          <button
            key={r}
            onClick={() => onPick(row[0])}
            disabled={future}
            onMouseEnter={() => setHover(r)}
            onMouseLeave={() => setHover((h) => (h === r ? null : h))}
            aria-label={`Week of ${row[0].toLocaleDateString(undefined, { month: "short", day: "numeric" })}`}
            aria-pressed={isSelected}
            style={{
              display: "grid", gridTemplateColumns: "repeat(7, 1fr)", width: "100%", padding: 0, margin: "1px 0",
              border: "none", borderRadius: 8, font: "inherit",
              cursor: future ? "default" : "pointer", opacity: future ? 0.4 : 1,
              ...cellLook(isSelected, lit && !isSelected),
            }}
          >
            {row.map((d) => {
              const k = dayKey(d);
              const inMonth = d.getMonth() === month.getMonth();
              return (
                <span key={k} style={{
                  position: "relative", height: 30, display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 12.5, color: lit ? "var(--ink)" : inMonth ? "var(--ink2)" : "var(--muted3)",
                }}>
                  <span style={{
                    width: 24, height: 24, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                    border: k === today ? "1px solid var(--accent)" : "1px solid transparent",
                  }}>{d.getDate()}</span>
                  {marked?.has(k) && <Dot faint={!inMonth} />}
                </span>
              );
            })}
          </button>
        );
      })}

      <Footer label="This week" onClick={() => onPick(mondayOf(new Date()))} />
    </Popover>
  );
}

/* -- Month and quarter ------------------------------------------------------ */

/** First day of the month picked. */
export function MonthPicker({ selected, marked, latest, onPick, onClose }: {
  selected: Date | null;
  marked?: Set<string>;
  latest?: Date;
  onPick: (first: Date) => void;
  onClose: () => void;
}) {
  return (
    <YearGrid
      label="Pick a month" thisLabel="This month" columns={4}
      selected={selected} marked={marked} latest={latest} onPick={onPick} onClose={onClose}
      cells={(year) => MONTHS_SHORT.map((name, m) => ({ name, start: new Date(year, m, 1), months: [m] }))}
      current={(now) => new Date(now.getFullYear(), now.getMonth(), 1)}
    />
  );
}

/** First day of the quarter picked. */
export function QuarterPicker({ selected, marked, latest, onPick, onClose }: {
  selected: Date | null;
  marked?: Set<string>;
  latest?: Date;
  onPick: (first: Date) => void;
  onClose: () => void;
}) {
  return (
    <YearGrid
      label="Pick a quarter" thisLabel="This quarter" columns={2}
      selected={selected} marked={marked} latest={latest} onPick={onPick} onClose={onClose}
      cells={(year) => [0, 1, 2, 3].map((q) => ({
        name: `Q${q + 1}`,
        sub: `${MONTHS_SHORT[q * 3]} – ${MONTHS_SHORT[q * 3 + 2]}`,
        start: new Date(year, q * 3, 1),
        months: [q * 3, q * 3 + 1, q * 3 + 2],
      }))}
      current={(now) => new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1)}
    />
  );
}

interface Cell { name: string; sub?: string; start: Date; months: number[] }

function YearGrid({ label, thisLabel, columns, selected, marked, latest, onPick, onClose, cells, current }: {
  label: string;
  thisLabel: string;
  columns: number;
  selected: Date | null;
  marked?: Set<string>;
  latest?: Date;
  onPick: (first: Date) => void;
  onClose: () => void;
  cells: (year: number) => Cell[];
  current: (now: Date) => Date;
}) {
  const [year, setYear] = useState(() => (selected ?? new Date()).getFullYear());
  /** "YYYY-MM" of every month with time logged. */
  const markedMonths = useMemo(() => new Set([...(marked ?? [])].map((k) => k.slice(0, 7))), [marked]);
  const now = new Date();
  const selectedTime = selected?.getTime();

  return (
    <Popover label={label} onClose={onClose} width={columns === 4 ? 264 : 240}>
      <Header
        title={String(year)} prevLabel="Previous year" nextLabel="Next year"
        onShift={(n) => setYear((y) => y + n)}
        nextDisabled={latest ? year + 1 > latest.getFullYear() : false}
      />
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: 4 }}>
        {cells(year).map((c) => {
          const isSelected = c.start.getTime() === selectedTime;
          const future = latest ? c.start.getTime() > latest.getTime() : false;
          const isNow = c.start.getTime() === current(now).getTime();
          const logged = c.months.some((m) => markedMonths.has(`${year}-${String(m + 1).padStart(2, "0")}`));
          return (
            <button
              key={c.name}
              onClick={() => onPick(c.start)}
              disabled={future}
              aria-pressed={isSelected}
              style={{
                position: "relative", padding: c.sub ? "10px 4px 12px" : "9px 4px 11px", borderRadius: 8, font: "inherit",
                fontSize: 12.5, cursor: future ? "default" : "pointer", opacity: future ? 0.4 : 1,
                color: "var(--ink2)", border: isNow ? "1px solid var(--accent)" : "1px solid transparent",
                ...cellLook(isSelected, false),
              }}
              onMouseEnter={(e) => { if (!future && !isSelected) e.currentTarget.style.background = "var(--tint)"; }}
              onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = "none"; }}
            >
              <div style={{ fontWeight: c.sub ? 600 : 400 }}>{c.name}</div>
              {c.sub && <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>{c.sub}</div>}
              {logged && <Dot />}
            </button>
          );
        })}
      </div>
      <Footer label={thisLabel} onClick={() => onPick(current(new Date()))} />
    </Popover>
  );
}

/* -- Shared pieces ---------------------------------------------------------- */

/** The floating panel. Outside mousedown or Escape closes it; the button that
 *  opens it should stop mousedown, or its click would reopen it at once. */
function Popover({ label, width, onClose, children }: {
  label: string; width: number; onClose: () => void; children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const down = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); };
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", down);
    document.addEventListener("keydown", key);
    return () => { document.removeEventListener("mousedown", down); document.removeEventListener("keydown", key); };
  }, [onClose]);
  return (
    <div ref={ref} role="dialog" aria-label={label} style={{
      position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 40, width,
      background: "var(--raised)", border: "1px solid var(--overlayLine)", borderRadius: 12,
      boxShadow: "var(--shadowOverlay)", padding: 12,
    }}>{children}</div>
  );
}

function Header({ title, prevLabel, nextLabel, onShift, nextDisabled }: {
  title: string; prevLabel: string; nextLabel: string; onShift: (n: number) => void; nextDisabled?: boolean;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
      <button onClick={() => onShift(-1)} aria-label={prevLabel} style={navBtn}>‹</button>
      <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ink)" }}>{title}</div>
      <button onClick={() => onShift(1)} aria-label={nextLabel} disabled={nextDisabled}
        style={{ ...navBtn, opacity: nextDisabled ? 0.35 : 1, cursor: nextDisabled ? "default" : "pointer" }}>›</button>
    </div>
  );
}

function Footer({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
      <button onClick={onClick} style={{
        fontSize: 12, padding: "4px 10px", borderRadius: 999, border: "1px solid var(--line2)",
        background: "var(--surface)", color: "var(--ink2)", cursor: "pointer",
      }}>{label}</button>
    </div>
  );
}

function Dot({ faint }: { faint?: boolean }) {
  return (
    <span style={{
      position: "absolute", bottom: 2, left: "50%", transform: "translateX(-50%)",
      width: 4, height: 4, borderRadius: "50%", background: "var(--accent)", opacity: faint ? 0.5 : 1,
    }} />
  );
}

function cellLook(selected: boolean, hovered: boolean): React.CSSProperties {
  return {
    background: selected ? "var(--accentTint)" : hovered ? "var(--tint)" : "none",
    outline: selected ? "1px solid var(--accentLine)" : "none",
  };
}

const navBtn: React.CSSProperties = {
  width: 28, height: 28, borderRadius: 7, border: "1px solid var(--line2)", background: "var(--surface)",
  color: "var(--ink2)", fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0,
};
